import { DependentFilter } from './dependentFilter.model';
import { Dashboard, WidgetInstance } from './dashboard.service';
import { SearchCondition, SearchGroup, SearchLogicalOperator } from '../Shell/PandoraSolutionBuilderAgent/model/search-query/search-form.contracts';
import { DashboardFiltersValues } from './dashboard-filters.service';
import type { SchemaService } from '../Shell/PandoraSolutionBuilderAgent/services/schema-service';
import type { MxFilterOperatorsFactory } from '../Shell/PandoraSolutionBuilderAgent/modules/DataQueryModel/column-filters/mx-grid-column-filter-operators-factory';
import { IScope } from 'angular';

export class DependentFiltersGroup {
	private filters: Map<string, DependentFilter>;

	constructor(
		private readonly schemaService: SchemaService,
		private readonly filterOperatorsFactory: MxFilterOperatorsFactory,
		private readonly dashboard: Dashboard,
		initialFilterValues?: DashboardFiltersValues
	) {
		this.filters = this.getDependentFilters(dashboard, initialFilterValues);
	}

	private getWidgetInstancesWithDependentFilters(dashboard: Dashboard) {
		return dashboard.widgetInstances.filter((widgetInstance) => !widgetInstance.hidden && widgetInstance.widget?.InteractiveFilter);
	}

	private getDependentFilters(dashboard: Dashboard, initialFilterValues?: DashboardFiltersValues) {
		const dependentFilters = new Map<string, DependentFilter>();

		const widgetInstancesWithDependentFilters = this.getWidgetInstancesWithDependentFilters(dashboard);
		widgetInstancesWithDependentFilters.forEach((influenceWidgetInstance) => {
			const initialValues = initialFilterValues?.dependent?.[influenceWidgetInstance.id];
			const dependentFilter = new DependentFilter(this.filterOperatorsFactory, influenceWidgetInstance, initialValues);
			dependentFilters.set(dependentFilter.id, dependentFilter);
		});

		return dependentFilters;
	}

	private async hackBacklogDependentGroup(widgetInstance: WidgetInstance, dependentGroup: SearchGroup) {
		try {
			const Backlog = 'f180a4d9-e2fb-ea11-5e84-000d3a23bb8e';
			const Backlog_CriticalTicketsCloseToEscalation = 'a1f5ed65-3561-ef11-0886-ec63d7895110';
			const Backlog_MostCriticalEscalatedTickets = 'ee7f9368-3161-ef11-0886-ec63d7895110';
			const Backlog_NumberOfInteractions30Days = 'c460dd2b-cc80-ef11-1086-ec63d7895110';
			const Backlog_ResponseTime30days = '37df8135-ca80-ef11-1086-ec63d7895110';

			const BacklogDataQueryIds = [
				Backlog,
				Backlog_CriticalTicketsCloseToEscalation,
				Backlog_MostCriticalEscalatedTickets,
				Backlog_NumberOfInteractions30Days,
				Backlog_ResponseTime30days,
			];

			if (!widgetInstance.dataModel.WidgetConfiguration.DataQueryObjId || !BacklogDataQueryIds.includes(widgetInstance.dataModel.WidgetConfiguration.DataQueryObjId)) {
				return;
			}

			const activityTypeNamesMap = new Map<string, string>();
			const keys = ['SPSActivityTypeTicket', 'SPSActivityTypeIncident', 'SPSActivityTypeServiceRequest', 'SPSActivityTypeBase'];
			for (const key of keys) {
				const typeInfo = await this.schemaService.getType(key);
				if (typeInfo) {
					activityTypeNamesMap.set(key, typeInfo.DisplayName);
				}
			}

			dependentGroup.Conditions?.forEach((condition) => {
				if (condition.Property === 'ActivityType') {
					condition.Property = 'Activity';

					condition.Value[0] = activityTypeNamesMap.get(condition.Value[0] as string) ?? condition.Value[0];
				}
			});
		} catch (e) {
			console.error(e);
		}
	}

	public getSearchGroupForWidgetInstance(widgetInstance: WidgetInstance): SearchGroup {
		const conditions: SearchCondition[] = [];

		this.filters.forEach((dependentFilter) => {
			conditions.push(...dependentFilter.getSearchConditions(widgetInstance));
		});

		return {
			Groups: [],
			Conditions: conditions,
			LogicalOperator: SearchLogicalOperator.Or,
		};
	}

	public async geConditionsForWidgetInstance(widgetInstance: WidgetInstance) {
		const conditions: SearchCondition[] = [];
		const dependentGroup = this.getSearchGroupForWidgetInstance(widgetInstance);

		if (dependentGroup.Conditions?.length) {
			//TODO: temporary solution for backlog data query. ActivityType (technical name) is replaced on Activity (display name), to be more user-friendly.
			//should be removed after the dependent filters will be replaced with interactive filters
			await this.hackBacklogDependentGroup(widgetInstance, dependentGroup);

			const dependentCondition = {
				Property: dependentGroup.Conditions[0].Property,
				Operator: this.filterOperatorsFactory.operators.ContainsAnyWord.value,
				Value: [] as unknown[],
			};

			dependentGroup.Conditions.forEach((condition) => {
				if (dependentCondition.Value[0]) {
					dependentCondition.Value[0] = `${dependentCondition.Value[0]},${condition.Value[0]}`;
				} else {
					dependentCondition.Value.push(condition.Value[0]);
				}
			});

			conditions.push(dependentCondition);
		}
		return conditions;
	}

	public getValues() {
		const values = {} as Record<string, string[]>;
		this.filters.forEach((dependentFilter) => {
			if (dependentFilter.hasValue()) {
				values[dependentFilter.id] = dependentFilter.getValue();
			}
		});
		return values;
	}

	public onChange($scope: IScope, handler: (dependentFilter: DependentFilter) => void) {
		this.filters.forEach((dependentFilter) => {
			$scope.$watchCollection(
				() => dependentFilter.getValue().join(''),
				(newVal, oldVal) => {
					if (newVal && oldVal && newVal !== oldVal) {
						handler(dependentFilter);
					}
				}
			);
		});
	}
}
